import axios from "axios";
import { toast } from "sonner";
import { cacheGet, readGetCache, enqueueMutation, isMutation, canQueueUrl, optimisticallyPatchCache } from "@/lib/offline";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  // Bearer tokens (in localStorage) are our default auth → no need for cookies.
  // Enabling withCredentials globally breaks CORS when the backend (or an edge
  // layer like Cloudflare) returns `Access-Control-Allow-Origin: *`.
  // For endpoints that require a cookie (Emergent Google Auth exchange),
  // pass `{ withCredentials: true }` explicitly on that call.
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("prosperr_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function isNetworkError(err) {
  // axios: no response → network / offline
  return !err?.response && (err?.code === "ERR_NETWORK" || err?.message === "Network Error" || !navigator.onLine);
}

api.interceptors.response.use(
  (res) => {
    // Cache idempotent GET responses for offline fallback (skip replays & auth)
    const cfg = res.config || {};
    const method = String(cfg.method || "").toLowerCase();
    // Don't re-cache responses that were served FROM cache (would overwrite optimistic patches)
    if (method === "get" && res.status >= 200 && res.status < 300 && !res.fromCache) {
      const url = cfg.url?.startsWith("http") ? cfg.url : `${API_BASE}${cfg.url || ""}`;
      // Don't cache auth or public endpoints where caching could leak data across users
      if (!/\/auth\//.test(cfg.url || "") && !/\/public\//.test(cfg.url || "")) {
        cacheGet(url, res.data).catch(() => {});
      }
    }
    return res;
  },
  async (err) => {
    const cfg = err?.config || {};
    const method = String(cfg.method || "").toLowerCase();
    const url = cfg.url || "";
    const alreadyReplayed = cfg.headers?.["X-Offline-Replay"] === "1";

    if (isNetworkError(err) && !alreadyReplayed) {
      // Offline GET → try cache
      if (method === "get") {
        const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
        const cached = await readGetCache(fullUrl);
        if (cached !== null && cached !== undefined) {
          return Promise.resolve({
            data: cached,
            status: 200,
            statusText: "OK (cache)",
            headers: {},
            config: cfg,
            fromCache: true,
          });
        }
      }
      // Offline mutation → queue for later replay
      if (isMutation(method) && canQueueUrl(url)) {
        const parsedData = cfg.data ? safeParse(cfg.data) : undefined;
        await enqueueMutation({
          method,
          url,
          data: parsedData,
          headers: stripAuth(cfg.headers),
        });
        // Optimistically patch the cached GET responses so the UI reflects the mutation
        await optimisticallyPatchCache(url, method, parsedData);
        toast.message("Enregistré hors-ligne", {
          description: "La modification sera synchronisée dès le retour du réseau.",
        });
        return Promise.resolve({
          data: { queued: true, offline: true, ...(parsedData || {}) },
          status: 202,
          statusText: "Accepted (queued)",
          headers: {},
          config: cfg,
          queued: true,
        });
      }
    }

    if (err?.response?.status === 401) {
      // don't auto-redirect to allow public pages
    }
    return Promise.reject(err);
  }
);

function safeParse(data) {
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return data; }
  }
  return data;
}

function stripAuth(headers = {}) {
  const out = {};
  for (const k of Object.keys(headers || {})) {
    if (String(k).toLowerCase() === "authorization") continue;
    out[k] = headers[k];
  }
  return out;
}

export default api;
